<script setup lang="ts">
import { useTheme } from 'vuetify'
import ScrollToTop from '@core/components/ScrollToTop.vue'
import initCore from '@core/initCore'
import { initConfigStore, useConfigStore } from '@core/stores/config'
import { hexToRgb } from '@core/utils/colorConverter'
/**
 * ⚠️ Explicit, not auto-imported — `unplugin-auto-import` scans `src/composables` at dev-server BOOT,
 * so a later-added composable is never injected into a running server.
 */
import { useNativeAppMode } from '@/composables/useNativeAppMode'
import { usePinchZoomGuard } from '@/composables/usePinchZoomGuard'
import { useVisualViewport } from '@/composables/useVisualViewport'
import RouteLoadingOverlay from '@/components/shared/RouteLoadingOverlay.vue'
import { installFormHistorySuppression } from '@/utils/suppressFormHistory'

const { global } = useTheme()

// ℹ️ Sync current theme with initial loader theme
initCore()
initConfigStore()

/**
 * The browser's pinch/double-tap magnifier is off app-wide: resizing the UI goes through the in-app
 * zoom (the Display sheet on phone, `PageZoomControls` on desktop), which re-lays the app out rather
 * than scaling the viewport. Registered here because iOS Safari ignores both `touch-action` and
 * `user-scalable=no` — see the composable.
 */
usePinchZoomGuard()

/**
 * Publishes the visual viewport — the area still visible once the software keyboard is up — as CSS
 * vars. `.alkokh-overlay-target` below is sized from them, which is what stops every dropdown in the
 * app from being measured against space the keyboard is covering. See the composable.
 */
useVisualViewport()

/**
 * No browser "previously typed values" popup under app fields — it covered dropdown results on
 * phones. Fields with an explicit `autocomplete` (the login form) are left alone. See the util.
 */
installFormHistorySuppression()

/**
 * Phone shell and installed PWA behave like a native app: browser back/forward (swipes, Android
 * system back) does nothing, and text selection, callouts, the context menu, tooltips and
 * scrollbars are off. Desktop tabs and Electron are untouched. See the composable.
 */
useNativeAppMode()

const configStore = useConfigStore()
</script>

<template>
  <VLocaleProvider :rtl="configStore.isAppRTL">
    <!-- ℹ️ This is required to set the background color of active nav link based on currently active global theme's primary -->
    <VApp :style="`--v-global-theme-primary: ${hexToRgb(global.current.value.colors.primary)}`">
      <RouterView />

      <ScrollToTop />
      <ActionLoaderOverlay />
      <!-- Tail-wagging cat while a page change is still loading (after a short delay). -->
      <RouteLoadingOverlay />
      <!-- App-wide QR scanner: opens over whatever page the user is on. -->
      <QrScannerOverlay />
      <!-- App-wide "which clinic?" prompt for unrestricted users (see branch.store). -->
      <BranchSelectionModal />
    </VApp>
    <Toaster />
  </VLocaleProvider>
</template>

<style lang="scss">
:root {
  --alkokh-page-zoom: 1;
}

.page-zoom-surface {
  inline-size: 100%;
  min-inline-size: 0;
  transform-origin: 0 0;
  transition:
    transform 0.18s ease,
    zoom 0.18s ease;
}

@supports (zoom: 1) {
  .page-zoom-surface {
    zoom: var(--alkokh-page-zoom);
  }
}

@supports not (zoom: 1) {
  .page-zoom-surface {
    inline-size: calc(100% / var(--alkokh-page-zoom));
    transform: scale(var(--alkokh-page-zoom));
  }
}

.layout-wrapper:fullscreen {
  overflow: auto;
  background: rgb(var(--v-theme-background));
}

.layout-wrapper:fullscreen .layout-content-wrapper,
.layout-wrapper:fullscreen.layout-nav-type-horizontal,
.layout-wrapper:fullscreen.layout-blank {
  min-block-size: 100dvh;
}

.alkokh-overlay-target {
  position: fixed;
  z-index: 5000;
  pointer-events: none;

  /*
   * ⚠️ SIZED TO THE VISUAL VIEWPORT, NOT `inset: 0`. `position: fixed` anchors to the LAYOUT
   * viewport, which iOS Safari does NOT shrink for the software keyboard — so `inset: 0` made this
   * layer claim space the keyboard was covering. The vars come from `useVisualViewport()`; their
   * fallbacks are exactly the old geometry, so a browser without `visualViewport` is unaffected.
   */
  inset-block-start: var(--app-vv-top, 0px);
  inset-inline: 0;
  block-size: var(--app-vv-height, 100dvh);
}

.alkokh-overlay-target > * {
  pointer-events: auto;
}

/*
 * ⚠️ `overflow-y: scroll` IS LOAD-BEARING. DO NOT DELETE IT AS DEAD STYLE.
 *
 * This layer never scrolls — it is `pointer-events: none` and its children are `position: fixed`.
 * The declaration exists so VUETIFY FINDS IT. Its connected location strategy derives the box it
 * places and sizes a menu against from `getScrollParents(contentEl)`, which accepts any ancestor
 * whose computed `overflow-y` is `scroll`. Without this, no ancestor of a teleported menu qualifies,
 * the list falls back to `document.documentElement` — the full layout viewport — and every dropdown
 * is placed and sized against space behind the keyboard and the tab bar. With it, Vuetify measures
 * against exactly the visible area and its own inline `max-height` comes out right, which is why
 * `styles.scss` no longer needs an `!important` cap.
 *
 * Safe because `.v-overlay` is `position: fixed` and a fixed ancestor is not a containing block for
 * a fixed descendant — this element reads as a scroll parent without clipping any overlay. Anything
 * teleported here that is `position: absolute` WOULD be clipped; keep such children fixed.
 *
 * Coarse pointers only: on desktop `overflow-y: scroll` would reserve a scrollbar gutter and shave
 * ~15px off every menu's width.
 */
@media (hover: none), (pointer: coarse) {
  .alkokh-overlay-target {
    overflow-y: scroll;
    overscroll-behavior: none;

    /* Keeps `clientWidth` at full width, so the gutter never narrows a menu. */
    scrollbar-width: none;
  }

  .alkokh-overlay-target::-webkit-scrollbar {
    display: none;
  }
}

.layout-wrapper:fullscreen .alkokh-overlay-target {
  z-index: 10000;
}

.layout-wrapper:fullscreen .v-overlay-container,
.layout-wrapper:fullscreen .v-overlay,
.layout-wrapper:fullscreen .flatpickr-calendar.open {
  z-index: 10001;
}
</style>
